/**
 * passiveEngine.js — Passive income coverage calculations.
 *
 * Pure functions — zero React.
 * Maps estimated dividend/distribution income from liquid investments
 * against the user's budget categories to show which expenses are
 * "covered" by passive income.
 *
 * Uses a configurable yield assumption (default 2% for a broad
 * ETF like XEQT — distributions, not total return).
 */

import { FF } from '../../utils/format';

/** Default distribution yield for a broad equity ETF */
export const DEFAULT_YIELD = 0.02;

/**
 * Calculate passive income coverage against expense categories.
 *
 * @param {object} s - app state
 * @param {array} proj - projection array from calc.run()
 * @param {number} [divYield] - annual distribution yield (default 2%)
 * @returns {object} coverage data
 */
export function calculateCoverage(s, proj, divYield = DEFAULT_YIELD) {
  const cats = (s.cats || []).slice();
  const pF = proj[proj.length - 1] || {};
  const currentYear = proj.find((d) => d.a === s.age) || proj[0] || {};

  // Current passive income (from today's investments)
  const currentLiq = currentYear.liq || s.nw0 || 0;
  const currentAnnual = currentLiq * divYield;
  const currentMonthly = currentAnnual / 12;

  // Projected passive income at plan end
  const projectedLiq = pF.liq || 0;
  const projectedAnnual = projectedLiq * divYield;
  const projectedMonthly = projectedAnnual / 12;

  // Sort categories by monthly cost (cheapest first = first to be "covered")
  const sorted = cats
    .filter((cat) => cat.a > 0)
    .sort((a, b) => a.a - b.a);

  // Build the ladder — accumulate expense totals and check coverage
  let runningTotal = 0;
  const ladder = sorted.map((cat) => {
    runningTotal += cat.a;
    const coveredNow = currentMonthly >= runningTotal;
    const coveredProjected = projectedMonthly >= runningTotal;
    const progressNow = runningTotal > 0
      ? Math.min(1, currentMonthly / runningTotal)
      : 0;

    return {
      id: cat.id,
      name: cat.n,
      monthlyCost: cat.a,
      color: cat.c,
      runningTotal,
      coveredNow,
      coveredProjected,
      progressNow,
    };
  });

  // Total monthly expenses
  const totalMonthly = cats.reduce((a, x) => a + x.a, 0);

  // Overall coverage percentage
  const overallCoverageNow = totalMonthly > 0
    ? Math.min(1, currentMonthly / totalMonthly)
    : 0;
  const overallCoverageProjected = totalMonthly > 0
    ? Math.min(1, projectedMonthly / totalMonthly)
    : 0;

  // Count covered categories
  const coveredCountNow = ladder.filter((l) => l.coveredNow).length;
  const coveredCountProjected = ladder.filter((l) => l.coveredProjected).length;

  // Find the "next to cover" category
  const nextTocover = ladder.find((l) => !l.coveredNow);
  const amountNeededForNext = nextTocover
    ? Math.max(0, nextTocover.runningTotal - currentMonthly)
    : 0;
  const investmentNeededForNext = amountNeededForNext > 0
    ? (amountNeededForNext * 12) / divYield
    : 0;

  return {
    currentMonthly,
    currentAnnual,
    projectedMonthly,
    projectedAnnual,
    totalMonthly,
    overallCoverageNow,
    overallCoverageProjected,
    coveredCountNow,
    coveredCountProjected,
    totalCategories: ladder.length,
    ladder,
    nextTocover,
    investmentNeededForNext,
    divYield,
  };
}

/**
 * Get a motivational message about passive income coverage.
 *
 * @param {object} coverage - result of calculateCoverage()
 * @returns {string}
 */
export function getCoverageMessage(coverage) {
  const { coveredCountNow, totalCategories, currentMonthly, nextTocover, investmentNeededForNext } = coverage;

  if (coveredCountNow === 0 && currentMonthly < 10) {
    return "Your passive income journey starts with your first invested dollar.";
  }

  if (coveredCountNow === 0 && nextTocover) {
    return `${FF(investmentNeededForNext)} more invested covers your ${nextTocover.name.toLowerCase()}. That's your next milestone.`;
  }

  if (coveredCountNow > 0 && coveredCountNow < totalCategories && nextTocover) {
    return `${coveredCountNow} expense${coveredCountNow > 1 ? "s" : ""} covered by passive income. ${FF(investmentNeededForNext)} more invested unlocks ${nextTocover.name.toLowerCase()}.`;
  }

  if (coveredCountNow === totalCategories) {
    return "Every expense covered by passive income. Your investments are working full-time.";
  }

  return `${FF(currentMonthly)}/mo in passive income and growing.`;
}
